import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { calculationAPI } from '../../lib/api';
import DdgCaptcha from '../DdgCaptcha';
import { DDG_CAPTCHA_REQUIRED_MESSAGE } from '../../lib/ddgCaptcha';
import { buildHomepageLeadSubmission } from '../../lib/homepageDraftLead';
import { HOMEPAGE_LEAD_SUCCESS } from '../../data/homepageDraft';
import { trackLeadContactSubmit } from '../../lib/metrikaGoals';

const METHODS = [
  { id: 'phone', label: 'Телефон', fieldLabel: 'Номер телефона', placeholder: '+7' },
  { id: 'email', label: 'E-mail', fieldLabel: 'E-mail', placeholder: 'name@company.ru' },
  { id: 'telegram', label: 'Telegram', fieldLabel: 'Ник в Telegram', placeholder: 'nickname' },
];

const EMPTY_ERRORS = {};

const HomepageLeadDialog = ({ open, onOpenChange, source = 'hero', presetTask = '', nonce = 0 }) => {
  const [method, setMethod] = useState('phone');
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [task, setTask] = useState('');
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState(EMPTY_ERRORS);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setMethod('phone');
      setName('');
      setContact('');
      setTask('');
      setConsent(false);
      setErrors(EMPTY_ERRORS);
      setSubmitError('');
      setIsSubmitting(false);
      setIsSubmitted(false);
      setCaptchaToken('');
      return;
    }

    setTask(presetTask || '');
    setErrors(EMPTY_ERRORS);
    setSubmitError('');
    setIsSubmitted(false);
  }, [open, nonce, presetTask]);

  const activeMethod = METHODS.find((item) => item.id === method) || METHODS[0];

  const selectMethod = (next) => {
    if (next === method) return;
    setMethod(next);
    setContact('');
    setErrors((prev) => ({ ...prev, contact: undefined }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    const result = buildHomepageLeadSubmission({
      name,
      contactMethod: method,
      contact,
      task,
      consent,
      source,
    });

    if (!result.ok) {
      setErrors(result.errors);
      return;
    }

    if (!captchaToken) {
      setSubmitError(DDG_CAPTCHA_REQUIRED_MESSAGE);
      return;
    }

    setErrors(EMPTY_ERRORS);
    setSubmitError('');
    setIsSubmitting(true);
    try {
      await calculationAPI.createCalculationRequest(
        result.payload.contact,
        result.payload.contactType,
        result.payload.calculatorData,
        result.payload.contactName,
        window.location.href,
        true,
        false,
        null,
        captchaToken,
      );
      trackLeadContactSubmit({ requestType: 'homepage_lead', leadSource: source });
      setName('');
      setContact('');
      setTask('');
      setConsent(false);
      setMethod('phone');
      setIsSubmitted(true);
    } catch (error) {
      setSubmitError(error.message || 'Не удалось отправить заявку. Попробуйте ещё раз.');
      captchaRef.current?.reset();
      setCaptchaToken('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[100dvh] w-screen max-w-none overflow-y-auto rounded-none border-0 bg-white p-6 sm:max-h-[min(100dvh-2rem,920px)] sm:w-full sm:max-w-[580px] sm:rounded-[28px] sm:p-12">
        {isSubmitted ? (
          <div className="py-8 text-center" data-testid="homepage-lead-success">
            <h2 className="text-2xl font-bold text-[#101828]">{HOMEPAGE_LEAD_SUCCESS.title}</h2>
            <p className="mt-4 text-base text-[#62748E]">{HOMEPAGE_LEAD_SUCCESS.text}</p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mx-auto w-full max-w-[484px]" data-testid="homepage-lead-form">
            <DialogHeader className="mb-6 space-y-0 pr-8 text-left">
              <DialogTitle className="text-[28px] font-bold leading-none text-[#0e0e0e] sm:text-[32px]">
                Выберите способ связи
              </DialogTitle>
              <DialogDescription className="sr-only">
                Имя, контакт и задача. Задачу можно не заполнять.
              </DialogDescription>
            </DialogHeader>

            <div
              role="radiogroup"
              aria-label="Способ связи"
              className="grid grid-cols-3 gap-1 rounded-2xl bg-[#ecebf2] p-1"
            >
              {METHODS.map((item) => {
                const selected = item.id === method;
                return (
                  <button
                    key={item.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => selectMethod(item.id)}
                    className={`h-11 rounded-xl text-sm font-semibold transition-colors ${
                      selected ? 'bg-white text-[#0e0e0e] shadow-sm' : 'text-[#0e0e0e]/60'
                    }`}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <Label htmlFor="homepage-lead-name" className="text-sm text-[#0e0e0e]">
                  Имя
                </Label>
                <Input
                  id="homepage-lead-name"
                  value={name}
                  placeholder="Имя"
                  onChange={(event) => setName(event.target.value)}
                  className="mt-2 h-12 rounded-xl border-[#e5e7eb] bg-white"
                />
                {errors.name && <p className="mt-1 text-sm text-destructive">{errors.name}</p>}
              </div>

              <div>
                <Label htmlFor="homepage-lead-contact" className="text-sm text-[#0e0e0e]">
                  {activeMethod.fieldLabel}
                </Label>
                <Input
                  id="homepage-lead-contact"
                  value={contact}
                  placeholder={activeMethod.placeholder}
                  type={method === 'email' ? 'email' : 'text'}
                  inputMode={method === 'phone' ? 'tel' : 'text'}
                  autoComplete={method === 'email' ? 'email' : method === 'phone' ? 'tel' : 'off'}
                  onChange={(event) => setContact(event.target.value)}
                  className="mt-2 h-12 rounded-xl border-[#e5e7eb] bg-white"
                />
                {errors.contact && <p className="mt-1 text-sm text-destructive">{errors.contact}</p>}
              </div>

              <div>
                <Label htmlFor="homepage-lead-task" className="text-sm text-[#0e0e0e]">
                  Ваша задача (необязательно)
                </Label>
                <Textarea
                  id="homepage-lead-task"
                  value={task}
                  onChange={(event) => setTask(event.target.value)}
                  className="mt-2 min-h-[96px] resize-y rounded-xl border-[#e5e7eb] bg-white"
                />
                <p className="mt-2 text-sm text-[#2f2f2f]/70">
                  Опишите задачу - подберём решение точнее и сразу предложим варианты
                </p>
              </div>
            </div>

            <DdgCaptcha ref={captchaRef} onToken={setCaptchaToken} />

            <button
              type="submit"
              disabled={isSubmitting || !captchaToken}
              className="mt-6 flex h-[50px] w-full items-center justify-center rounded-2xl bg-[#F08517] text-base font-semibold text-white transition-colors hover:bg-[#d97412] disabled:opacity-70"
            >
              {isSubmitting ? 'Отправка...' : 'Оставить заявку'}
            </button>
            {submitError && <p className="mt-2 text-sm text-destructive">{submitError}</p>}

            <div className="mt-4">
              <div className="flex items-start gap-2">
                <Checkbox
                  id="homepage-lead-consent"
                  checked={consent}
                  onCheckedChange={(checked) => setConsent(checked === true)}
                  className="mt-0.5 h-3.5 w-3.5"
                  aria-invalid={Boolean(errors.consent)}
                />
                <Label htmlFor="homepage-lead-consent" className="text-sm font-normal leading-5 text-[#2f2f2f]">
                  Согласен(а) на{' '}
                  <Link to="/privacy" className="underline underline-offset-2" onClick={(event) => event.stopPropagation()}>
                    обработку персональных данных
                  </Link>
                </Label>
              </div>
              {errors.consent && <p className="mt-1 text-sm text-destructive">{errors.consent}</p>}
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default HomepageLeadDialog;
